/**
 * Module 132 (WP-132.4) — what the Milestones popup, the home icon and (later)
 * the Badges popup show, from the engine's state. Pure.
 *
 * One row per milestone. A row's medal is the highest rung reached: a tier
 * (0–3), then Platinum with a ★ count. `ready` is its lowest rung that is
 * reached, has coins and is not yet paid — the one its Claim pays. Every number
 * comes from `meta/milestones.ts`, so this view and the engine cannot disagree.
 */
import { MILESTONES, PLAYER_TITLE_AT, PLAYER_TITLE_STEP } from '../constants/economy';
import { claimable, isLevelClaimed, levelCoins, levelKey, levelTarget, levelsReached, nextTarget, counterOf, parseLevelKey, } from './milestones';
/**
 * WP-132.10 — the rows with a rung waiting, newest first: by the date it was earned
 * (an undated, seeded one is oldest), then the higher rung, then list order. The
 * Milestones popup lists them in this order and the home peek shows the first, so
 * the peek always names the newest badge.
 */
export function readyRows(rows) {
    const order = new Map(MILESTONES.map((m, i) => [m.id, i]));
    return rows
        .filter((r) => r.ready !== null)
        .sort((a, b) => (b.readyAt ?? '').localeCompare(a.readyAt ?? '') ||
        (b.ready?.index ?? 0) - (a.ready?.index ?? 0) ||
        (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}
/** The medal a milestone shows: its tier and ★ steps, or null when nothing is earned. */
export function medalOf(reached, tierCount, oneOff) {
    if (reached <= 0)
        return null;
    // A one-off wears a fixed Gold medal: it has no tier ladder to climb.
    if (oneOff)
        return { tier: 2, stars: 0 };
    if (reached <= tierCount)
        return { tier: reached - 1, stars: 0 };
    return { tier: 3, stars: reached - tierCount };
}
/** WP-132.11 — the title `badges` earns, and how far the next one is. */
export function titleOf(badges) {
    const top = PLAYER_TITLE_AT.length - 1;
    let index = 0;
    for (let i = 0; i <= top; i += 1)
        if (badges >= PLAYER_TITLE_AT[i])
            index = i;
    if (index < top) {
        return {
            index,
            stars: 0,
            badges,
            at: PLAYER_TITLE_AT[index],
            nextAt: PLAYER_TITLE_AT[index + 1],
        };
    }
    const stars = Math.floor((badges - PLAYER_TITLE_AT[top]) / PLAYER_TITLE_STEP);
    const at = PLAYER_TITLE_AT[top] + stars * PLAYER_TITLE_STEP;
    return { index, stars, badges, at, nextAt: at + PLAYER_TITLE_STEP };
}
/** The newest 3 dated levels. Same day: the later milestone-list entry and higher rung first. */
function recentLevels(state) {
    const out = [];
    for (const [key, date] of Object.entries(state.earned ?? {})) {
        const parsed = parseLevelKey(key);
        if (!parsed)
            continue;
        const { def, index } = parsed;
        const medal = medalOf(index + 1, def.tiers.length, def.every <= 0);
        if (medal) {
            out.push({ id: def.id, index, ...medal, date, order: MILESTONES.indexOf(def) });
        }
    }
    out.sort((a, b) => a.date !== b.date ? (a.date < b.date ? 1 : -1) : b.order - a.order || b.index - a.index);
    return out.slice(0, 3).map(({ order: _order, ...rest }) => rest);
}
export function buildMilestonesView(state, legacy) {
    const ready = claimable(state, legacy);
    const rows = MILESTONES.map((def) => {
        const reached = levelsReached(state, def, legacy);
        const oneOff = def.every <= 0;
        const medal = medalOf(reached, def.tiers.length, oneOff);
        const next = nextTarget(state, def.id, legacy);
        const lowReady = ready.find((l) => l.id === def.id) ?? null;
        // The bar is toward the next rung: a tier from 0, a ★ step from the step behind it.
        // A finished one-off is full.
        let pct = 100;
        let barTier = oneOff ? 2 : 3;
        let shown = null;
        if (next) {
            const from = next.kind === 'step' ? levelTarget(def, next.index - 1) : 0;
            shown = { value: Math.max(0, next.value - from), target: next.target - from };
            pct = Math.round(Math.max(0, Math.min(1, shown.value / Math.max(1, shown.target))) * 100);
            barTier = Math.min(3, next.index);
        }
        const tiers = def.tiers.map((_, index) => {
            const key = levelKey(def, index);
            return {
                index,
                key,
                target: levelTarget(def, index),
                coins: levelCoins(def, index),
                reached: reached > index,
                claimed: isLevelClaimed(state, key, legacy),
                date: state.earned?.[key] ?? null,
            };
        });
        // The newest dated rung this badge has reached (ties: the higher rung).
        let earnedAt = null;
        for (let i = 0; i < reached; i += 1) {
            const d = state.earned?.[levelKey(def, i)];
            if (d !== undefined && (earnedAt === null || d >= earnedAt))
                earnedAt = d;
        }
        return {
            id: def.id,
            reached,
            tier: medal?.tier ?? null,
            stars: medal?.stars ?? 0,
            value: counterOf(state, def.id),
            ready: lowReady,
            next,
            oneOff,
            done: oneOff && next === null,
            pct,
            barTier,
            shown,
            readyAt: lowReady ? (state.earned?.[lowReady.key] ?? null) : null,
            tiers,
            step: !oneOff && def.coins.length > 0
                ? { every: def.every, coins: levelCoins(def, def.tiers.length) }
                : null,
            earnedAt,
        };
    });
    const badges = rows.reduce((sum, r) => sum + r.reached, 0);
    return { rows, ready, title: titleOf(badges), recent: recentLevels(state) };
}
